import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    AlignmentType,
    Table,
    TableCell,
    TableRow,
    Header,
    Footer,
    UnderlineType,
    LevelFormat,
    PageNumber,
    ImageRun,
    IImageOptions,
    SectionType,
    VerticalAlign,
    WidthType,
    convertInchesToTwip,
    NumberFormat,
    ExternalHyperlink,
    InternalHyperlink,
    Bookmark,
    BorderStyle,
    ISpacingProperties,
} from 'docx';

import { createdBy } from './generate-docx.const';

interface TableData {
    labels?: string[];
    values: string[][];
}

interface MultiEventTableData {
    labels?: string[];
    values: string[][] | Paragraph[][];
}

interface Sentence {
    text: string;
    bold?: boolean;
    italics?: boolean;
    highlight?: boolean;
    underline?: boolean;
    superScript?: boolean;
    color?: string;
    hyperlink?: string;
    bookMark?: string;
    anchor?: string;
    textSize?: number;
}

interface BulletItems {
    text: Sentence[];
    level?: number;
    textSize?: number;
}

interface HeadingArgs {
    text: string;
    level: keyof typeof HeadingLevel;
    pageBreakBefore?: boolean;
    color?: string;
    bookMark?: string;
    isItalics?: boolean;
    breakValue?: number;
}

interface ParagraphArgs {
    text: Sentence[];
    newPage?: boolean;
    indent?: number;
    textSize?: number;
    breakValue?: number;
    spacing?: {
        before?: number;
        after?: number;
        line?: number;
    };
}
interface BulletListArgs {
    items: BulletItems[];
    spacing?: {
        before?: number;
        after?: number;
        line?: number;
    };
}

interface NumberListArgs {
    items: BulletItems[];
    spacing?: {
        before?: number;
        after?: number;
        line?: number;
    };
}

interface HyperlinkArgs {
    text: string;
    url: string;
    color?: string;
    spacing?: {
        before?: number;
        after?: number;
        line?: number;
    };
}

interface TableArgs {
    data: TableData;
}

interface MultiEventTableArgs {
    multiEventData: MultiEventTableData;
}

interface StylizedHeadingArgs {
    text: string;
    headingType: string;
}

interface DocumentMetaData {
    date: Date;
}

interface TitlePageData {
    title: string;
    subtitle?: string;
    documentMetaData?: DocumentMetaData;
    logo?: IImageOptions;
}

interface TextRunOptions {
    textSize?: number;
    breakValue?: number;
    defaultColor?: string;
}

type TemplateArgs =
    | HeadingArgs
    | ParagraphArgs
    | BulletListArgs
    | NumberListArgs
    | HyperlinkArgs
    | TableArgs
    | MultiEventTableArgs
    | StylizedHeadingArgs;

export interface Theme {
    name: string;
    colors: {
        primary: string;
        accent: string;
        tertiary: string;
        divider?: string;
        insight?: string; // Added for insights color
        titlePage?: {
            backgroundColor?: string;
            textColor?: string;
        };
        tableLabelBackgroundColor?: string;
        ascendingMetric?: string;
        descendingMetric?: string;
    };
    fonts: {
        heading1: string;
        heading2: string;
        heading3?: string;
        heading4?: string;
        body: string;
        titlePage?: {
            title?: string;
            subtitle?: string;
            body?: string;
        };
    };
    sizes: {
        heading1: number;
        heading2: number;
        heading3?: number;
        heading4?: number;
        body: number;
        titlePage?: {
            title?: number;
            subtitle?: number;
            body?: number;
        };
    };
}

export const defaultTheme: Theme = {
    name: 'PendoTheme',
    colors: {
        primary: '#000000',
        accent: '#D14F72',
        tertiary: '#6a6c75',
        titlePage: {
            backgroundColor: '#FFFFFF',
            textColor: '#000000',
        },
    },
    fonts: {
        heading1: 'Calibri',
        heading2: 'Calibri',
        body: 'Arial',
        titlePage: {
            title: 'Arial Black',
            subtitle: 'Arial',
            body: 'Arial',
        },
    },
    sizes: {
        heading1: 14,
        heading2: 12,
        body: 12,
    },
};

export const numberingConfig = {
    reference: 'numbering-config',
    levels: [
        {
            level: 0,
            format: LevelFormat.DECIMAL,
            text: '%1',
            alignment: AlignmentType.LEFT,
            style: {
                paragraph: {
                    indent: { left: 720, hanging: 360 },
                },
            },
        },
        {
            level: 1,
            format: LevelFormat.DECIMAL,
            text: '%2.',
            alignment: AlignmentType.LEFT,
            style: {
                paragraph: {
                    indent: { left: 1440, hanging: 360 },
                },
            },
        },
        {
            level: 2,
            format: LevelFormat.DECIMAL,
            text: '%3)',
            alignment: AlignmentType.LEFT,
            style: {
                paragraph: {
                    indent: { left: 2160, hanging: 360 },
                },
            },
        },
        {
            level: 3,
            format: LevelFormat.UPPER_LETTER,
            text: '%4)',
            alignment: AlignmentType.LEFT,
            style: {
                paragraph: {
                    indent: { left: 2880, hanging: 360 },
                },
            },
        },
    ],
};

export function createStyles (theme: Theme) {
    return {
        default: {
            heading1: {
                run: {
                    size: theme.sizes.heading1 * 2,
                    bold: true,
                    italics: false,
                    color: theme.colors.accent,
                    font: theme.fonts.heading1,
                },
                paragraph: {
                    spacing: {
                        before: 120,
                        after: 120,
                    },
                },
            },
            heading2: {
                run: {
                    size: theme.sizes.heading2 * 2,
                    bold: true,
                    color: theme.colors.accent,
                    font: theme.fonts.heading2,
                },
                paragraph: {
                    spacing: {
                        before: 160,
                        after: 100,
                    },
                },
            },
            footer: {
                run: {
                    alignment: AlignmentType.LEFT,
                    size: 12,
                    color: theme.colors.primary,
                },
            },
            title: {
                run: {
                    size: 76,
                    bold: true,
                    font: theme.fonts.titlePage?.title || theme.fonts.heading1,
                    color: theme.colors.accent || theme.colors.titlePage?.textColor,
                },
            },
            hyperlink: {
                run: {
                    color: theme.colors.accent,
                    underline: { type: UnderlineType.SINGLE },
                },
                paragraph: {
                    spacing: {
                        before: 120,
                        after: 480,
                        line: 276,
                    },
                },
            },
            listParagraph: {
                run: {
                    color: theme.colors.primary,
                },
            },
            document: {
                run: {
                    size: theme.sizes.body * 2,
                    font: theme.fonts.body,
                    color: theme.colors.primary,
                },
                paragraph: {
                    alignment: AlignmentType.LEFT,
                    spacing: {
                        line: 276,
                        after: 120,
                    },
                },
            },
        },
    };
}

export function createPageHeader (text: string, theme: Theme = defaultTheme) {
    return new Header({
        children: [
            new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({ text, bold: true, color: theme.colors.primary })],
            }),
        ],
    });
}

export function createFooter (footerText: string = 'Company Brief', theme: Theme = defaultTheme) {
    return new Footer({
        children: [
            new Table({
                width: {
                    size: 100,
                    type: WidthType.PERCENTAGE,
                },
                columnWidths: [4505, 4505],
                rows: [
                    new TableRow({
                        children: [
                            new TableCell({
                                width: {
                                    size: 4505,
                                    type: WidthType.DXA,
                                },
                                borders: {
                                    top: {
                                        size: 6,
                                        style: 'single',
                                        color: '#D14F72',
                                    },
                                    bottom: {
                                        size: 0,
                                        style: 'none',
                                        color: 'FFFFFF',
                                    },
                                    left: {
                                        size: 0,
                                        style: 'none',
                                        color: 'FFFFFF',
                                    },
                                    right: {
                                        size: 0,
                                        style: 'none',
                                        color: 'FFFFFF',
                                    },
                                },
                                children: [
                                    new Paragraph({
                                        children: [
                                            new TextRun({ break: 1 }),
                                            new TextRun({
                                                text: footerText,
                                                color: theme.colors.primary,
                                                size: 20,
                                            }),
                                        ],
                                        alignment: AlignmentType.LEFT,
                                    }),
                                ],
                            }),
                            new TableCell({
                                width: {
                                    size: 4505,
                                    type: WidthType.DXA,
                                },
                                borders: {
                                    top: {
                                        size: 6,
                                        style: 'single',
                                        color: '#D14F72',
                                    },
                                    bottom: {
                                        size: 0,
                                        style: 'none',
                                        color: 'FFFFFF',
                                    },
                                    left: {
                                        size: 0,
                                        style: 'none',
                                        color: 'FFFFFF',
                                    },
                                    right: {
                                        size: 0,
                                        style: 'none',
                                        color: 'FFFFFF',
                                    },
                                },
                                children: [
                                    new Paragraph({
                                        children: [
                                            new TextRun({ break: 1 }),
                                            new TextRun({
                                                children: [PageNumber.CURRENT],
                                                color: theme.colors.primary,
                                                size: 20,
                                            }),
                                        ],
                                        alignment: AlignmentType.RIGHT,
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            }),
        ],
    });
}

export function createBulletList (
    items: BulletItems[],
    spacing?: { before?: number; after?: number; line?: number },
): Paragraph[] {
    return items.map((item: BulletItems, idx) => {
        const children = createTextRunsFromSentences(item.text, {
            textSize: item.textSize,
            defaultColor: '000000',
        });

        return new Paragraph({
            children,
            bullet: {
                level: item.level ?? 0,
            },
            spacing: idx === 0 && spacing ? spacing : undefined,
        });
    });
}

export function createNumberList (
    items: BulletItems[],
    spacing?: { before?: number; after?: number; line?: number },
): Paragraph[] {
    return items.map((item: BulletItems, idx) => {
        const children = createTextRunsFromSentences(item.text, {
            textSize: item.textSize,
            defaultColor: '000000',
        });

        return new Paragraph({
            children,
            numbering: {
                reference: numberingConfig.reference,
                level: item.level ?? 0,
            },
            spacing: idx === 0 && spacing ? spacing : undefined,
        });
    });
}

export function createParagraph (
    text: Sentence[],
    newPage: boolean = false,
    indent?: number,
    textSize?: number,
    breakValue?: number,
    spacing?: ISpacingProperties,
) {
    const children = createTextRunsFromSentences(text, {
        textSize,
        breakValue,
        defaultColor: defaultTheme.colors.accent,
    });

    return new Paragraph({
        children,
        pageBreakBefore: newPage,
        indent: indent !== undefined ? { left: indent } : undefined,
        spacing: spacing || undefined,
    });
}

export function createHeading ({
    text,
    level = 'HEADING_1',
    pageBreakBefore = false,
    color,
    bookMark = '',
    breakValue,
}: HeadingArgs): Paragraph {
    const defaultColor = level === 'HEADING_1' ? defaultTheme.colors.primary : defaultTheme.colors.accent;
    if (bookMark.length) {
        return new Paragraph({
            heading: HeadingLevel[level],
            alignment: AlignmentType.LEFT,
            pageBreakBefore,
            children: [
                new Bookmark({
                    id: bookMark,
                    children: [
                        new TextRun({
                            text,
                            color: color || defaultColor,
                            break: breakValue || undefined,
                            size: level === 'HEADING_1' ? 24 * 2 : 14 * 2,
                        }),
                    ],
                }),
            ],
        });
    }

    return new Paragraph({
        heading: HeadingLevel[level],
        alignment: AlignmentType.LEFT,
        pageBreakBefore,
        children: [
            new TextRun({
                text,
                color: color || defaultColor,
                break: breakValue,
                size: level === 'HEADING_1' ? 24 * 2 : 14 * 2,
            }),
        ],
    });
}

export function createImage (options: IImageOptions) {
    return new Paragraph({
        children: [
            new ImageRun({
                ...options,
            }),
        ],
    });
}

export function createHyperlink (
    text: string,
    url: string,
    spacing?: { before?: number; after?: number; line?: number },
) {
    return new Paragraph({
        style: 'Hyperlink',
        spacing: spacing
            ? {
                before: spacing.before,
                after: spacing.after,
                line: spacing.line,
            }
            : undefined,
        children: [
            new ExternalHyperlink({
                children: [
                    new TextRun({
                        text,
                        style: 'Hyperlink',
                    }),
                ],
                link: url,
            }),
        ],
    });
}

export function createTable (data: TableData) {
    return new Table({
        width: {
            size: 100,
            type: WidthType.PERCENTAGE,
        },
        rows: [
            new TableRow({
                tableHeader: true,
                children: (data.labels ?? []).map(
                    (item) =>
                        new TableCell({
                            children: [new Paragraph({ text: item })],
                            margins: {
                                left: 100,
                            },
                            verticalAlign: VerticalAlign.CENTER,
                        }),
                ),
            }),
            ...data.values.map(
                (row) =>
                    new TableRow({
                        children: row.map(
                            (item) =>
                                new TableCell({
                                    children: [new Paragraph({ text: item })],
                                    margins: {
                                        left: 100,
                                    },
                                    verticalAlign: VerticalAlign.CENTER,
                                }),
                        ),
                    }),
            ),
        ],
    });
}

export function createTitlePage (data: TitlePageData, theme: Theme = defaultTheme) {
    const { title, subtitle, documentMetaData } = data;
    const logoImage = new ImageRun({
        type: 'png',
        data: Buffer.from(createdBy, 'base64'),
        transformation: {
            width: 176,
            height: 38,
        },
    });
    const sections: (Paragraph | Table)[] = [
        new Paragraph({
            heading: 'Title',
            spacing: {
                before: convertInchesToTwip(3),
            },
            children: [logoImage],
        }),
        new Paragraph({
            spacing: {
                before: convertInchesToTwip(0.25),
                after: 5,
            },
            children: [
                new TextRun({
                    text: title,
                    size: 76,
                    bold: true,
                    font: theme.fonts.titlePage?.title || theme.fonts.heading1,
                    color: theme.colors.accent || theme.colors.titlePage?.textColor,
                }),
            ],
        }),
    ];
    if (subtitle) {
        sections.push(
            new Paragraph({
                spacing: {
                    after: convertInchesToTwip(0.1),
                },
                children: [
                    new TextRun({
                        text: subtitle,
                        size: 30,
                        bold: false,
                        font: theme.fonts.titlePage?.subtitle || theme.fonts.heading2,
                        color: theme.colors.titlePage?.textColor || theme.colors.primary,
                    }),
                ],
            }),
        );
    }
    if (documentMetaData) {
        const { date } = documentMetaData;
        const metaDataTable = new Table({
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
            columnWidths: [3003, 3003, 3003],
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            width: {
                                size: 3003,
                                type: WidthType.DXA,
                            },
                            borders: {
                                top: {
                                    size: 0,
                                    style: 'none',
                                    color: '#D14F72',
                                },
                                bottom: {
                                    size: 0,
                                    style: 'none',
                                    color: 'FFFFFF',
                                },
                                left: {
                                    size: 0,
                                    style: 'none',
                                    color: 'FFFFFF',
                                },
                                right: {
                                    size: 0,
                                    style: 'none',
                                    color: 'FFFFFF',
                                },
                            },
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `Generated on ${date.toLocaleDateString(undefined, {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                            })}`,
                                            color: theme.colors.tertiary || theme.colors.titlePage?.textColor,
                                            size: 22,
                                        }),
                                    ],
                                    alignment: AlignmentType.LEFT,
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        });
        sections.push(metaDataTable);
    }

    return {
        properties: {
            titlePage: true,
            page: {
                margin: {
                    top: 0,
                    header: 0,
                },
                pageNumbers: {
                    start: 1,
                    formatType: NumberFormat.DECIMAL,
                },
            },
        },
        children: sections,
    };
}

export function createMultiEventTable (data: MultiEventTableData): Table {
    const borderStyle = {
        top: {
            size: 1,
            style: BorderStyle.SINGLE,
            color: '#d9d9d9',
        },
        bottom: {
            size: 1,
            style: BorderStyle.SINGLE,
            color: '#d9d9d9',
        },
        left: {
            size: 1,
            style: BorderStyle.SINGLE,
            color: '#d9d9d9',
        },
        right: {
            size: 1,
            style: BorderStyle.SINGLE,
            color: '#d9d9d9',
        },
    };

    return new Table({
        columnWidths: [2300, 1362, 2670, 2670],
        width: {
            size: 100,
            type: WidthType.PERCENTAGE,
        },
        borders: borderStyle,
        rows: [
            new TableRow({
                tableHeader: true,
                children: (data.labels ?? []).map(
                    (item) =>
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [new TextRun({ text: item, bold: true })],
                                    spacing: { after: 0 },
                                }),
                            ],
                            margins: {
                                left: 100,
                                bottom: 100,
                                top: 100,
                                right: 100,
                            },
                            borders: borderStyle,
                            verticalAlign: VerticalAlign.CENTER,
                            shading: {
                                fill: '#efefef',
                            },
                        }),
                ),
            }),
            ...data.values.map(
                (row) =>
                    new TableRow({
                        children: row.map(
                            (item) =>
                                new TableCell({
                                    children: typeof item === 'string' ? [new Paragraph({ text: item })] : [item],
                                    margins: {
                                        left: 100,
                                        bottom: 100,
                                        top: 100,
                                        right: 100,
                                    },
                                    borders: borderStyle,
                                    verticalAlign: VerticalAlign.CENTER,
                                }),
                        ),
                    }),
            ),
        ],
    });
}

export interface Template {
    function:
        | typeof createFooter
        | typeof createBulletList
        | typeof createNumberList
        | typeof createParagraph
        | typeof createHeading
        | typeof createTable
        | typeof createHyperlink
        | typeof createMultiEventTable
        | typeof createStylizedSubHeading;
    args: TemplateArgs;
}

export function saveFile (blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = filename || 'download';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
}

const fantasyTemplate: Template[] = [
    {
        function: createHeading,
        args: {
            text: '📜 The Tale of {{heroName}}',
            level: 'HEADING_1',
        },
    },
    {
        function: createParagraph,
        args: {
            text: [
                {
                    text: 'In the realm of ',
                },
                {
                    text: '{{location}} ',
                    bold: true,
                    italics: true,
                },
                {
                    text: 'there lived a noble ',
                },
                {
                    text: '{{race}} ',
                    italics: true,
                },
                {
                    text: 'named ',
                },
                {
                    text: '{{heroName}}. ',
                    bold: true,
                },
                {
                    text: 'With only a ',
                },
                {
                    text: '{{weapon}}',
                    italics: true,
                },
                {
                    text: ' and their loyal companion, a ',
                },
                {
                    text: '{{companion}}',
                    italics: true,
                },
                {
                    text: ', they set out on a quest to retrieve the ',
                },
                {
                    text: '{{questItem}}.',
                    bold: true,
                },
            ],
        },
    },
    {
        function: createHeading,
        args: {
            text: '🗺️ The Journey',
            level: 'HEADING_2',
            pageBreakBefore: true,
        },
    },
    {
        function: createBulletList,
        args: {
            items: [
                {
                    text: [{ text: 'Navigate the Forest of Whispers' }],
                    level: 0,
                },
                {
                    text: [{ text: 'Cross the Chasm of Echoes' }],
                    level: 0,
                },
                {
                    text: [{ text: 'Defeat the minions of darkness' }],
                    level: 0,
                },
                {
                    text: [{ text: 'Find the hidden vault of the ancients' }],
                    level: 0,
                },
                {
                    text: [{ text: 'Secure the ' }, { text: '{{questItem}}', bold: true }],
                    level: 0,
                },
            ],
        },
    },
    {
        function: createHeading,
        args: {
            text: '⚔️ The Encounter',
            level: 'HEADING_2',
            pageBreakBefore: true,
        },
    },
    {
        function: createParagraph,
        args: {
            text: [
                {
                    text: 'Suddenly, they encountered the dreaded ',
                },
                {
                    text: '{{villain}}. ',
                    bold: true,
                    underline: true,
                    italics: true,
                },
                {
                    text: 'The battle was fierce. With a mighty roar of ',
                },
                {
                    text: '"{{battleCry}}", ',
                    bold: true,
                },
                {
                    text: 'our hero charged with their ',
                },
                {
                    text: '{{weapon}}, ',
                    italics: true,
                },
                {
                    text: 'determined to protect their realm.',
                },
            ],
        },
    },
    {
        function: createHeading,
        args: {
            text: '💥 The Final Battle',
            level: 'HEADING_2',
            pageBreakBefore: false,
        },
    },
    {
        function: createParagraph,
        args: {
            text: [
                {
                    text: 'Steel clashed. Magic blazed. ',
                    bold: true,
                },
                {
                    text: 'The earth trembled beneath their feet. ',
                    italics: true,
                },
                {
                    text: 'But in the end, ',
                },
                {
                    text: '{{heroName}} ',
                    bold: true,
                },
                {
                    text: 'emerged victorious, holding the ',
                },
                {
                    text: '{{questItem}} ',
                    bold: true,
                },
                {
                    text: 'high above their head.',
                },
            ],
        },
    },
    {
        function: createHeading,
        args: {
            text: '📊 FINAL STATS',
            level: 'HEADING_1',
            pageBreakBefore: true,
        },
    },
    {
        function: createTable,
        args: {
            data: {
                labels: ['Attribute', 'Value'],
                values: [
                    ['Hero', '{{heroName}}'],
                    ['Race', '{{race}}'],
                    ['Weapon', '{{weapon}}'],
                    ['Companion', '{{companion}}'],
                    ['Villain', '{{villain}}'],
                    ['Quest Item', '{{questItem}}'],
                ],
            },
        },
    },
];

export function generateDocumentFromTemplate (
    template: Template[],
    data: Record<string, string>,
): (Paragraph | Table)[] {
    const templatedContent = [];
    for (const item of template) {
        const { function: func, args } = item;
        if (func === createParagraph) {
            if ('text' in args) {
                const paragraphArgs = args as ParagraphArgs;
                const { text, newPage = false, indent, textSize, breakValue, spacing } = paragraphArgs;
                if (Array.isArray(text)) {
                    const formattedText = (text as Sentence[]).map((sentence) => {
                        const {
                            text,
                            bold = false,
                            color = defaultTheme.colors.primary,
                            italics = false,
                            underline = false,
                            superScript = false,
                            highlight = false,
                            hyperlink = undefined,
                            anchor = undefined,
                            textSize = undefined,
                        } = sentence;

                        return {
                            text: bindTokens(text, data),
                            bold,
                            color,
                            italics,
                            underline,
                            superScript,
                            highlight,
                            hyperlink,
                            anchor,
                            textSize,
                        };
                    });
                    templatedContent.push(func(formattedText, newPage, indent, textSize, breakValue, spacing));
                }
            }
        }
        if (func === createHeading) {
            if ('text' in args && 'level' in args) {
                const headingArgs = args as HeadingArgs;
                const { text, level, pageBreakBefore = false, color, bookMark, breakValue } = headingArgs;
                if (typeof text === 'string') {
                    const boundText = bindTokens(text, data);
                    templatedContent.push(
                        func({ text: boundText, level, pageBreakBefore, color, bookMark, breakValue }),
                    );
                }
            }
        }
        if (func === createBulletList || func === createNumberList) {
            if ('items' in args) {
                const { items, spacing } = args;
                if (Array.isArray(items)) {
                    const formattedItems = items.map((item) => {
                        return {
                            text: item.text.map((sentence: Sentence) => ({
                                text: bindTokens(sentence.text, data),
                                bold: sentence.bold,
                                italics: sentence.italics,
                                underline: sentence.underline,
                                superScript: sentence.superScript,
                                color: sentence.color,
                                hyperlink: sentence.hyperlink,
                                bookMark: sentence.bookMark,
                                anchor: sentence.anchor,
                            })),
                            level: item.level,
                            textSize: item.textSize,
                        };
                    });
                    const result = (func as typeof createBulletList | typeof createNumberList)(formattedItems, spacing);
                    templatedContent.push(...(Array.isArray(result) ? result : [result]));
                }
            }
        }
        if (func === createTable) {
            if ('data' in args) {
                const { values = [] } = args.data;
                let boundValues;
                if (values.length) {
                    boundValues = values.map((valueRow: string[]) => {
                        return valueRow.map((value: string) => {
                            return bindTokens(value, data);
                        });
                    });
                    const newArgs = {
                        ...args.data,
                        values: boundValues,
                    };
                    templatedContent.push(func(newArgs));
                }
            }
        }
        if (func === createMultiEventTable) {
            if ('multiEventData' in args) {
                const { multiEventData } = args as MultiEventTableArgs;
                templatedContent.push((func as typeof createMultiEventTable)(multiEventData));
            }
        }
        if (func === createHyperlink) {
            if ('text' in args && 'url' in args) {
                const { text, url, spacing } = args as HyperlinkArgs;
                if (typeof text === 'string' && typeof url === 'string') {
                    const boundText = bindTokens(text, data);
                    const boundUrl = bindTokens(url, data);
                    templatedContent.push(func(boundText, boundUrl, spacing));
                }
            }
        }
        if (func === createStylizedSubHeading) {
            if ('text' in args && 'headingType' in args) {
                const { text, headingType } = args as StylizedHeadingArgs;
                if (typeof text === 'string') {
                    const boundText = bindTokens(text, data);
                    templatedContent.push(func({ text: boundText, headingType }));
                }
            }
        }
    }

    return templatedContent.filter((item) => typeof item !== 'undefined').flat();
}

export function bindTokens (template: string, data: Record<string, string>): string {
    return template.replace(/{{(.*?)}}/g, (__, key) => data[key.trim()] || '');
}

export async function createDocument (
    data: {
        title: string;
        subtitle?: string;
        content?: Record<string, string>;
    },
    theme: Theme = defaultTheme,
    includeHeader: boolean = false,
) {
    const { title, subtitle, content } = data;
    if (typeof content !== 'undefined') {
        const docContent = generateDocumentFromTemplate(fantasyTemplate, content);
        const styles = createStyles(theme);
        const doc = new Document({
            styles,
            numbering: {
                config: [numberingConfig],
            },
            sections: [
                {
                    ...createTitlePage({
                        title,
                        ...(subtitle && { subtitle }),
                        documentMetaData: {
                            date: new Date(
                                new Date().toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                }),
                            ),
                        },
                    }),
                },
                {
                    properties: {
                        type: SectionType.NEXT_PAGE,
                    },
                    ...(includeHeader && {
                        headers: {
                            default: createPageHeader(title, theme),
                        },
                    }),
                    footers: {
                        default: createFooter(),
                    },
                    children: [...docContent],
                },
            ],
        });

        const blob = await Packer.toBlob(doc);
        saveFile(blob, `${title}.docx`);
    }
}

export function createTextRunsFromSentences (sentences: Sentence[], options: TextRunOptions = {}) {
    const { breakValue, defaultColor = '000000' } = options;
    let { textSize } = options;
    const shouldResetTextSize = textSize === undefined;

    return sentences.map((sentence) => {
        const { text, bold, italics, underline, superScript, color, hyperlink, bookMark, anchor, highlight } = sentence;

        if (textSize === undefined && sentence.textSize !== undefined) {
            textSize = sentence.textSize; // pull textSize from the sentence if not provided in options
        }
        const textRun = new TextRun({
            text,
            bold,
            italics,
            underline: underline ? { type: UnderlineType.SINGLE } : undefined,
            superScript,
            color: color || defaultColor,
            size: textSize ? textSize * 2 : 10 * 2,
            style: anchor ? 'Hyperlink' : undefined,
            break: breakValue,
            highlight: highlight ? 'lightGray' : undefined,
        });

        if (hyperlink) {
            return new ExternalHyperlink({
                children: [textRun],
                link: hyperlink,
            });
        }

        if (anchor) {
            return new InternalHyperlink({
                children: [textRun],
                anchor,
            });
        }

        if (bookMark) {
            return new Bookmark({
                id: bookMark,
                children: [textRun],
            });
        }

        if (shouldResetTextSize) {
            textSize = undefined; // reset textSize for next iteration
        }

        return textRun;
    });
}

export function createTableParagraph (args: TemplateArgs): Paragraph {
    const paragraphArgs = args as ParagraphArgs;
    const { text, newPage = false, indent, textSize, breakValue, spacing } = paragraphArgs;
    const formattedText = (text as Sentence[]).map((sentence) => {
        const {
            text,
            bold = false,
            color = defaultTheme.colors.primary,
            italics = false,
            underline = false,
            superScript = false,
            highlight = false,
            hyperlink = undefined,
            anchor = undefined,
        } = sentence;

        return {
            text,
            bold,
            color,
            italics,
            underline,
            superScript,
            highlight,
            hyperlink,
            anchor,
        };
    });

    return createParagraph(formattedText, newPage, indent, textSize, breakValue, spacing);
}

export function createStylizedSubHeading (stylizedHeadingArgs: StylizedHeadingArgs): Table {
    const { text, headingType } = stylizedHeadingArgs;

    const formattedText = new TextRun({
        text: ` ${text}`,
        bold: true,
        size: headingType === 'section' ? 28 : 24,
        color: headingType === 'section' ? '#ffffff' : '#e6416a',
        font: 'Calibri',
        allCaps: headingType === 'subSection',
    });

    const borderOptions = {
        left: {
            style: headingType === 'subSection' ? BorderStyle.SINGLE : BorderStyle.NIL,
            size: 12,
            color: '#ff587e',
        },
        top: {
            style: BorderStyle.NIL,
        },
        right: {
            style: BorderStyle.NIL,
        },
        bottom: {
            style: BorderStyle.NIL,
        },
    };

    const heading = new TableCell({
        children: [
            new Paragraph({
                children: [formattedText],
            }),
        ],
        borders: borderOptions,
        margins: {
            top: 135,
            bottom: 0,
            left: 0,
            right: 0,
        },
        verticalAlign: VerticalAlign.CENTER,
        shading: {
            fill: headingType === 'section' ? '#e6416a' : '#fcedf1',
        },
    });

    return new Table({
        columnWidths: [9000],
        width: {
            size: 9070,
            type: WidthType.DXA,
        },
        rows: [
            new TableRow({
                children: [heading],
            }),
        ],
    });
}
